import { Icon } from '@iconify/react'
import { env } from '@/lib/env'
import {
  CUSTOM_DOMAIN_CNAME_TARGET,
  isReservedCustomDomainHost,
  normalizeCustomDomainInput,
} from '@/lib/campaign-custom-domain'
import styles from './CampanhaEditPage.module.css'

type CampaignCustomDomainSectionProps = {
  value: string
  publicPageUrl: string
  onChange: (value: string) => void
}

export function CampaignCustomDomainSection({
  value,
  publicPageUrl,
  onChange,
}: CampaignCustomDomainSectionProps) {
  const hasExternalRedirect = publicPageUrl.trim() !== ''
  const trimmed = value.trim()
  const normalized = trimmed === '' ? null : normalizeCustomDomainInput(value)
  const showInputError =
    trimmed !== '' &&
    (normalized === null || isReservedCustomDomainHost(normalized, env.publicAppUrl))

  return (
    <section className={styles.editSection} aria-labelledby="section-dominio-personalizado">
      <h2 id="section-dominio-personalizado" className={styles.editSectionTitle}>
        <Icon className={styles.editSectionTitleIcon} icon="mdi:web" aria-hidden />
        Domínio personalizado
      </h2>
      <div className={styles.editSectionBody}>
        <p className={styles.editInfoBanner}>
          Quando alguém acessar este domínio, verá só a página pública desta campanha
          (incluindo conta, checkout, perfil e ganhador). Qualquer outro endereço neste
          host também abre esta campanha. O painel da instituição não fica disponível
          aqui. Salve a campanha e, na hospedagem do domínio, crie o CNAME. O
          apontamento vale assim que o DNS propagar.
        </p>

        <div className={styles.editField}>
          <label className={styles.editLabel} htmlFor="campaign-custom-domain">
            Domínio
            <span className={styles.editLabelHint}>(somente o host, sem http)</span>
          </label>
          <input
            id="campaign-custom-domain"
            type="text"
            className={styles.editInput}
            placeholder="Ex.: sorteio.cliente.com.br"
            maxLength={253}
            autoComplete="off"
            spellCheck={false}
            value={value}
            aria-invalid={showInputError}
            onChange={(event) => onChange(event.target.value)}
            onBlur={() => {
              if (normalized) {
                onChange(normalized)
              }
            }}
          />
          {showInputError ? (
            <p className={styles.editFieldError} role="alert">
              Informe um subdomínio válido, sem http, porta ou caminho. Não use o
              endereço do app nem {CUSTOM_DOMAIN_CNAME_TARGET}.
            </p>
          ) : null}
        </div>

        <div className={styles.editDnsBox}>
          <h3 className={styles.editSubsectionTitle}>Apontamento DNS</h3>
          <p className={styles.editDnsText}>
            Na hospedagem do seu domínio (Registro.br, Cloudflare, HostGator, etc.),
            crie um registro <strong>CNAME</strong> no <strong>mesmo host</strong> que
            você preencheu acima, apontando para:
          </p>
          <code className={styles.editDnsCode}>{CUSTOM_DOMAIN_CNAME_TARGET}</code>
          <p className={styles.editDnsText}>
            Use um subdomínio (ex.: sorteio.seudominio.com.br). O domínio raiz (@) em
            geral não aceita CNAME. Digite o host exatamente como no DNS, com ou sem
            www — os dois não são o mesmo endereço.
          </p>
          <p className={styles.editDnsText}>
            Não configure certificado SSL na hospedagem: o HTTPS é emitido no destino
            {` ${CUSTOM_DOMAIN_CNAME_TARGET}`}. Só o CNAME é necessário aí. Sem o
            apontamento correto, o navegador bloqueia o acesso seguro.
          </p>
        </div>

        {hasExternalRedirect ? (
          <p className={styles.editWarningBanner} role="status">
            A campanha também tem uma URL externa na aba Página pública. No domínio
            personalizado a landing desta campanha continua neste site; o redirecionamento
            externo não é aplicado nesse host.
          </p>
        ) : null}
      </div>
    </section>
  )
}
